import PubNub from 'pubnub';
import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getMessagingProviderKeyConfigs } from '@/redux/modules/config';
import { getBrowserSessionCookieSuffix } from '@/redux/modules/console';
import {
    handleCatalogOnMessage,
    handleCatalogOnPresence,
    handlePubnubCatalogOnStatus,
} from '@/redux/modules/liveAuctionEvents';
import { LiveAuctionEvent } from '@/types/LiveAuctionEvent';

import { FilterOptions, pubnubFilters } from './filters';

type Props = {
    /** The catalog to subscribe to for real-time events (onMessage, onStatus, onPresence etc) */
    catalogId: number;
    /** Applies a filter to ignore certain live auction messages defined in ./hooks/filters */
    limitMessagesFilter?: FilterOptions;
    /** Subscribe to occupancy updates for catalog and submit presence for user */
    subscribeToPresence: boolean;
};

/**
 * Connects to PubNub to subscribe to live catalog events, updates redux with messages + presence + connection status
 */
const usePubNubCatalog = ({ catalogId, limitMessagesFilter, subscribeToPresence }: Props) => {
    const dispatch = useAppDispatch();
    const pubnubClient = React.useRef<PubNub>(null);
    const channel = `catalog.${catalogId}`;

    const {
        pubnub: { subscribeKey },
    } = useAppSelector(getMessagingProviderKeyConfigs);

    /**
     * Generate session cookie for PubNub client
     */
    const browserSession = useAppSelector(getBrowserSessionCookieSuffix);
    const sessionId = React.useMemo(() => {
        return `${uuidv4()}${browserSession}-${channel}`;
    }, [browserSession, channel]);

    /**
     * Handlers for PubNub events
     */
    const onMessage = React.useCallback(
        (messageEvent: PubNub.Subscription.Message) => {
            dispatch(handleCatalogOnMessage(catalogId)(messageEvent.message as LiveAuctionEvent));
        },
        [catalogId, dispatch]
    );

    const onPresence = React.useCallback(
        (presenceEvent: PubNub.Subscription.Presence) => {
            if (presenceEvent.action === 'join' || presenceEvent.action === 'leave') {
                dispatch(
                    handleCatalogOnPresence(catalogId)({
                        channel: presenceEvent.channel,
                        occupancy: presenceEvent.occupancy,
                    })
                );
            }
        },
        [catalogId, dispatch]
    );

    const onStatus = React.useCallback(
        (statusEvent: PubNub.Status | PubNub.StatusEvent) => {
            dispatch(handlePubnubCatalogOnStatus(catalogId)(statusEvent));
        },
        [catalogId, dispatch]
    );

    // Initialize client
    React.useEffect(() => {
        if (subscribeKey) {
            pubnubClient.current = new PubNub({
                subscribeKey,
                uuid: sessionId,
            });
        }
    }, [sessionId, subscribeKey]);

    // Set Message Event Filter
    React.useEffect(() => {
        if (subscribeKey) {
            const filterExpression = limitMessagesFilter ? pubnubFilters[limitMessagesFilter] : '';
            pubnubClient.current?.setFilterExpression(filterExpression);
        }
    }, [limitMessagesFilter, subscribeKey]);

    // Add event listeners
    React.useEffect(() => {
        if (subscribeKey) {
            pubnubClient.current?.addListener({
                message: onMessage,
                presence: onPresence,
                status: onStatus,
            });
        }

        return () => {
            pubnubClient.current?.removeListener({
                message: onMessage,
                presence: onPresence,
                status: onStatus,
            });
        };
    }, [onMessage, onPresence, onStatus, subscribeKey]);

    // Subscribe to channel
    React.useEffect(() => {
        if (subscribeKey) {
            pubnubClient.current?.subscribe({
                channels: [channel],
                // subscribe to changes in occupancy
                withPresence: subscribeToPresence,
            });

            if (subscribeToPresence) {
                // get the current occupancy count on connect
                pubnubClient.current?.hereNow({ channels: [channel] }, (_status, response) => {
                    dispatch(
                        handleCatalogOnPresence(catalogId)({
                            channel,
                            occupancy: response.channels[channel].occupancy,
                        })
                    );
                });
            }
        }

        return () => {
            pubnubClient.current?.unsubscribeAll();
        };
    }, [dispatch, catalogId, channel, subscribeToPresence, subscribeKey]);
};

export default usePubNubCatalog;
